import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@material-ui/core";
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: "1200px",
    margin: "0 auto",
    height: "100vh",
    overflowY: "auto",
    overflowX: "hidden",
  },
  header: {
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.dragover": {
      borderColor: theme.palette.secondary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  resultCard: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: "400px",
    overflow: "auto",
  },
  chunkItem: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  logContainer: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    maxHeight: "300px",
    overflowY: "auto",
    overflowX: "hidden",
    fontFamily: "monospace",
    fontSize: "0.875rem",
  },
  configSection: {
    marginBottom: theme.spacing(3),
  },
  hiddenInput: {
    display: "none",
  },
  scrollableContent: {
    maxHeight: "200px",
    overflowY: "auto",
    overflowX: "hidden",
  },
  manifestContainer: {
    maxHeight: "300px",
    overflowY: "auto",
    overflowX: "auto",
    backgroundColor: "#f8f9fa",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
  },
  configInput: {
    marginBottom: theme.spacing(2),
  },
  configGrid: {
    marginTop: theme.spacing(2),
  },
}));

// Configuration constants
const SERVER_URL = "http://localhost:8081/Api/V1";
const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
const CLIENT_ID = "World Digital Now";

const IngestionSystemPOC = () => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [chunks, setChunks] = useState([]);
  const [manifest, setManifest] = useState(null);

  // Configuration state
  const [serverUrl, setServerUrl] = useState(SERVER_URL);
  const [chunkSize, setChunkSize] = useState(CHUNK_SIZE);
  const [clientId, setClientId] = useState(CLIENT_ID);

  // Utility functions
  const getContentType = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    const contentTypes = {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      doc: "application/msword",
      txt: "text/plain",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      zip: "application/zip",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      mp4: "video/mp4",
      avi: "video/x-msvideo",
      mov: "video/quicktime",
    };
    return contentTypes[ext] || "application/octet-stream";
  };

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setUploadLogs((prev) => [...prev, { message, type, timestamp }]);
  };

  const generateUUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        var r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  };

  // API functions
  const getSignedUrl = async (fileName, contentType) => {
    addLog(`Getting signed URL for: ${fileName}`);

    const response = await fetch(`${serverUrl}/Files/URLs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileName: fileName,
        contentType: contentType,
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to get signed URL: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    addLog("Received signed URL successfully", "success");
    return data;
  };

  const uploadChunk = async (chunk, chunkIndex, fileId) => {
    const chunkFileName = `${fileId}_chunk_${chunkIndex
      .toString()
      .padStart(4, "0")}`;

    addLog(`Getting signed URL for chunk ${chunkIndex}...`);
    const chunkSignedUrlResponse = await getSignedUrl(
      chunkFileName,
      "application/octet-stream"
    );

    addLog(`Uploading chunk ${chunkIndex} (${chunk.length} bytes)...`);

    const response = await fetch(chunkSignedUrlResponse.url, {
      method: chunkSignedUrlResponse.method,
      body: chunk,
      headers: {
        ...chunkSignedUrlResponse.headers,
        "Content-Type": "application/octet-stream",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to upload chunk ${chunkIndex}: ${response.status} ${response.statusText}`
      );
    }

    addLog(`Chunk ${chunkIndex} uploaded successfully`, "success");
    return {
      index: chunkIndex,
      name: chunkSignedUrlResponse.url,
      size: chunk.length,
      start: chunkIndex * chunkSize,
      end: Math.min((chunkIndex + 1) * chunkSize, selectedFile.size),
      status: "uploaded",
    };
  };

  const createManifest = (fileId, fileName, fileSize, chunks, clientId) => {
    return {
      fileId: fileId,
      fileName: fileName,
      fileSize: fileSize,
      fileType: getContentType(fileName),
      totalChunks: chunks.length,
      chunkSize: chunkSize,
      chunks: chunks.map((chunk) => ({
        index: chunk.index,
        name: chunk.name,
        size: chunk.size,
        start: chunk.start,
        end: chunk.end,
      })),
      uploadTimestamp: new Date().toISOString(),
      clientId: clientId,
      originalName: fileName,
    };
  };

  const uploadManifest = async (manifest, fileId) => {
    const manifestBuffer = new Blob([JSON.stringify(manifest, null, 2)], {
      type: "application/json",
    });
    const manifestFileName = `${fileId}_manifest.json`;

    addLog("Getting signed URL for manifest...");
    const manifestSignedUrlResponse = await getSignedUrl(
      manifestFileName,
      "application/json"
    );

    addLog("Uploading manifest...");

    const response = await fetch(manifestSignedUrlResponse.url, {
      method: manifestSignedUrlResponse.method,
      body: manifestBuffer,
      headers: {
        ...manifestSignedUrlResponse.headers,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to upload manifest: ${response.status} ${response.statusText}`
      );
    }

    addLog("Manifest uploaded successfully", "success");
    return manifestSignedUrlResponse.url;
  };

  const notifyAssembly = async (manifestUrl, clientIdParam) => {
    addLog("Notifying server to assemble file...");

    const response = await fetch(`${serverUrl}/Archives/Uploads/Manifests`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": clientIdParam,
      },
      body: JSON.stringify({
        manifestUrl: manifestUrl,
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to notify assembly: ${response.status} ${response.statusText}`
      );
    }

    const result = await response.json();
    addLog(`Server response: ${JSON.stringify(result)}`, "success");
    return result;
  };

  // File handling
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setUploadProgress(0);
    setUploadResult(null);
    setUploadLogs([]);
    setChunks([]);
    setManifest(null);
    addLog(`File selected: ${file.name} (${file.size} bytes)`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Main upload function
  const uploadFileInChunks = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadLogs([]);

    const fileId = generateUUID();
    addLog(
      `Starting chunked upload for ${selectedFile.name} (${selectedFile.size} bytes)`
    );
    addLog(`File ID: ${fileId}`);

    try {
      // Calculate chunks
      const totalChunks = Math.ceil(selectedFile.size / chunkSize);
      addLog(`File will be split into ${totalChunks} chunks`);

      const uploadedChunks = [];

      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, selectedFile.size);
        const chunk = selectedFile.slice(start, end);

        const chunkResult = await uploadChunk(chunk, i, fileId);
        uploadedChunks.push(chunkResult);

        // Update progress
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        setUploadProgress(progress);
        addLog(`Progress: ${progress}% (${i + 1}/${totalChunks} chunks)`);
      }

      setChunks(uploadedChunks);

      // Create and upload manifest
      const manifestData = createManifest(
        fileId,
        selectedFile.name,
        selectedFile.size,
        uploadedChunks,
        clientId
      );
      setManifest(manifestData);
      const manifestUrl = await uploadManifest(manifestData, fileId);

      // Notify server to assemble
      const result = await notifyAssembly(manifestUrl, clientId);

      setUploadResult(result);
      addLog(
        `Upload completed successfully! Final file URL: ${result.finalBlobUrl}`,
        "success"
      );
    } catch (error) {
      addLog(`Upload failed: ${error.message}`, "error");
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadResult(null);
    setUploadLogs([]);
    setChunks([]);
    setManifest(null);
    setIsUploading(false);
  };

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      <Container className={classes.container}>
        <Paper elevation={3} style={{ padding: "24px", marginBottom: "24px" }}>
          <Box className={classes.header}>
            <Typography variant="h4" gutterBottom>
              Ingestion System POC
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Chunked File Upload System for Large Files
            </Typography>
          </Box>

          {/* Configuration Section */}
          <Card className={classes.configSection}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <Grid container spacing={3} className={classes.configGrid}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Server URL"
                    value={serverUrl}
                    onChange={(e) => setServerUrl(e.target.value)}
                    disabled={isUploading}
                    className={classes.configInput}
                    variant="outlined"
                    size="small"
                    helperText="API endpoint for file operations"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Chunk Size (MB)"
                    type="number"
                    value={chunkSize / (1024 * 1024)}
                    onChange={(e) =>
                      setChunkSize(parseFloat(e.target.value) * 1024 * 1024)
                    }
                    disabled={isUploading}
                    className={classes.configInput}
                    variant="outlined"
                    size="small"
                    inputProps={{ min: 0.1, step: 0.1 }}
                    helperText="Size of each chunk in megabytes"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Client ID"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    disabled={isUploading}
                    className={classes.configInput}
                    variant="outlined"
                    size="small"
                    helperText="Client identification string"
                  />
                </Grid>
              </Grid>

              {/* Display current configuration */}
              <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Configuration:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>Server:</strong> {serverUrl}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>Chunk Size:</strong>{" "}
                      {(chunkSize / 1024 / 1024).toFixed(1)} MB
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2">
                      <strong>Client ID:</strong> {clientId}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* File Selection */}
          <Card>
            <CardContent>
              <Box
                className={classes.uploadArea}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUploadIcon style={{ fontSize: 48, color: "#0076FF" }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile
                    ? selectedFile.name
                    : "Select or drop a file here"}
                </Typography>
                {selectedFile && (
                  <Typography variant="body2" color="textSecondary">
                    Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  style={{ marginTop: 16 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Browse Files
                </Button>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                className={classes.hiddenInput}
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleFileSelect(e.target.files[0]);
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* Upload Controls */}
          {selectedFile && (
            <Box mt={2} display="flex" gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={uploadFileInChunks}
                disabled={isUploading}
                startIcon={<CloudUploadIcon />}
              >
                {isUploading ? "Uploading..." : "Start Upload"}
              </Button>
              <Button
                variant="outlined"
                onClick={resetUpload}
                disabled={isUploading}
                startIcon={<RefreshIcon />}
              >
                Reset
              </Button>
            </Box>
          )}

          {/* Progress */}
          {isUploading && (
            <Box className={classes.progressContainer}>
              <Typography variant="body2" gutterBottom>
                Upload Progress: {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <Card className={classes.resultCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <CheckCircleIcon style={{ color: "green", marginRight: 8 }} />
                  Upload Complete
                </Typography>
                <Typography variant="body2">
                  <strong>Final URL:</strong> {uploadResult.finalBlobUrl}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Chunks Information */}
          {chunks.length > 0 && (
            <Card className={classes.resultCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Chunks Information ({chunks.length} total)
                </Typography>
                <Box className={classes.scrollableContent}>
                  {chunks.map((chunk, index) => (
                    <Box key={index} className={classes.chunkItem}>
                      <Typography variant="body2">
                        Chunk {chunk.index}: {chunk.size} bytes ({chunk.start} -{" "}
                        {chunk.end})
                        <Chip
                          size="small"
                          label={chunk.status}
                          color="primary"
                          className={classes.statusChip}
                        />
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Manifest Information */}
          {manifest && (
            <Card className={classes.resultCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Manifest Information
                </Typography>
                <Box className={classes.manifestContainer}>
                  <pre
                    style={{
                      fontSize: "0.875rem",
                      margin: 0,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {JSON.stringify(manifest, null, 2)}
                  </pre>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Upload Logs */}
          {uploadLogs.length > 0 && (
            <Card className={classes.resultCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upload Logs
                </Typography>
                <Box className={classes.logContainer}>
                  {uploadLogs.map((log, index) => (
                    <div
                      key={index}
                      style={{
                        color:
                          log.type === "error"
                            ? "red"
                            : log.type === "success"
                            ? "green"
                            : "black",
                        marginBottom: "4px",
                      }}
                    >
                      [{log.timestamp}] {log.message}
                    </div>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default IngestionSystemPOC;
